import React, { useCallback, useMemo, useState } from "react";
import { Button, Table, Tag } from "antd";
import { ColumnProps } from "antd/lib/table/interface";
import StudentName from "./components/StudentName";
import ReviewDrawer from "./components/ReviewDrawer";
import useModalHandler from "utils/useModalHandler";
import useRequest from "../../../../../utils/useRequest";
import { getStudentExamPaperList } from "../../../../../service/exam";
import { toNumber } from "../../../../../service/service";
import moment from "moment";

interface EvaluateTableProps {
    batchNo: string;
    examId: string;
    courseNo: string;
    courseSeq: string;
}

export default function EvaluateTable({
    batchNo,
    examId,
    courseNo,
    courseSeq
}: EvaluateTableProps) {
    const [current, setCurrent] = useState<number>(0);
    const drawer = useModalHandler();

    const [data, { loading }] = useRequest(() =>
        getStudentExamPaperList({ courseNo, courseSeq, examId, batchNo })
    );

    const onReview = useCallback(
        (index: number) => {
            drawer.open();
            setCurrent(index);
        },
        [drawer]
    );

    const goPrev = useCallback(
        () => setCurrent(current => Math.max(0, current - 1)),
        []
    );

    const goNext = useCallback(
        () =>
            setCurrent(current =>
                Math.min(toNumber(data?.length) - 1, current + 1)
            ),
        [data]
    );

    const currentData = useMemo(() => {
        if (!data) return;
        if (current === -1) return;
        return data[current];
    }, [current, data]);

    const columns: ColumnProps<any>[] = [
        {
            title: "姓名",
            dataIndex: "name",
            render: (name, item) => (
                <StudentName
                    name={name}
                    ban={item.ban}
                    stuHaveExamFlag={item.stuHaveExamFlag}
                />
            )
        },
        { title: "学号", dataIndex: "studentNo" },
        {
            title: "提交状态",
            dataIndex: "studnetSubmit",
            filters: [
                { text: "未参考", value: "2" },
                { text: "已提交", value: "1" },
                { text: "未提交", value: "0" }
            ],
            onFilter: (value, record) => value.includes(record.studnetSubmit),
            render: (value: string) =>
                value === "1" ? (
                    <Tag color="#87d068">已提交</Tag>
                ) : (
                    <Tag>未提交</Tag>
                )
        },
        {
            title: "批改状态",
            dataIndex: "checkFlag",
            filters: [
                { text: "已批改", value: "1" },
                { text: "未批改", value: "0" }
            ],
            onFilter: (value, record) => value.includes(record.checkFlag),
            render: (value: string) =>
                value === "1" ? (
                    <Tag color="#87d068">已批改</Tag>
                ) : (
                    <Tag>未批改</Tag>
                )
        },
        {
            title: "完成时间",
            dataIndex: "endTime",
            render: text =>
                text ? moment(text).format("YYYY-MM-DD HH:mm") : ""
        },
        { title: "客观题分数", dataIndex: "objectiveScore" },
        { title: "分数", dataIndex: "score" },
        {
            title: "操作",
            render: (text, item, index) => (
                <Button type="primary" onClick={() => onReview(index)}>
                    查看
                </Button>
            )
        }
    ];

    return (
        <>
            <Table
                rowKey="studentNo"
                columns={columns}
                dataSource={data || []}
                pagination={false}
                bordered
                loading={loading}
            />

            {drawer.render(
                <ReviewDrawer
                    goNext={current + 1 === data?.length ? undefined : goNext}
                    goPrev={current === 0 ? undefined : goPrev}
                    batchNo={batchNo}
                    paperData={currentData}
                />
            )}
        </>
    );
}
